import React, { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateClientCommunication } from '@/hooks/useClientCommunications';
import type { CommunicationType } from '@/types/client';

interface LogCommunicationFormProps {
  clientId: string;
  trigger: React.ReactNode;
}

const COMM_TYPES: { value: CommunicationType; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'call', label: 'Phone Call' },
  { value: 'text', label: 'Text / SMS' },
  { value: 'in_person', label: 'In Person' },
];

export const LogCommunicationForm: React.FC<LogCommunicationFormProps> = ({
  clientId,
  trigger,
}) => {
  const [open, setOpen] = useState(false);
  const [commType, setCommType] = useState<CommunicationType>('email');
  const [summary, setSummary] = useState('');
  const [notes, setNotes] = useState('');
  const [occurredAt, setOccurredAt] = useState(() => {
    const now = new Date();
    return now.toISOString().slice(0, 16); // datetime-local format
  });

  const createMutation = useCreateClientCommunication(clientId);

  const handleSubmit = useCallback(() => {
    if (!summary.trim()) return;
    createMutation.mutate(
      {
        client_id: clientId,
        comm_type: commType,
        summary: summary.trim(),
        notes: notes.trim() || undefined,
        occurred_at: new Date(occurredAt).toISOString(),
      },
      {
        onSuccess: () => {
          setOpen(false);
          setSummary('');
          setNotes('');
          setCommType('email');
        },
      }
    );
  }, [clientId, commType, summary, notes, occurredAt, createMutation]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-surface-1 border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider text-sm">
            Log Communication
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          {/* Type */}
          <div>
            <label htmlFor="comm-type" className="text-[10px] font-label uppercase tracking-wider text-muted-foreground block mb-1">
              Type
            </label>
            <Select value={commType} onValueChange={(v) => setCommType(v as CommunicationType)}>
              <SelectTrigger id="comm-type" className="bg-surface-2 border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface-1 border-border">
                {COMM_TYPES.map((ct) => (
                  <SelectItem key={ct.value} value={ct.value}>
                    {ct.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date/time */}
          <div>
            <label htmlFor="comm-when" className="text-[10px] font-label uppercase tracking-wider text-muted-foreground block mb-1">
              Date & Time
            </label>
            <Input
              id="comm-when"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              className="bg-surface-2 border-border font-mono text-sm"
            />
          </div>

          {/* Summary */}
          <div>
            <label htmlFor="comm-summary" className="text-[10px] font-label uppercase tracking-wider text-muted-foreground block mb-1">
              Summary
            </label>
            <Input
              id="comm-summary"
              placeholder="Brief description..."
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="bg-surface-2 border-border"
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="comm-notes" className="text-[10px] font-label uppercase tracking-wider text-muted-foreground block mb-1">
              Notes (optional)
            </label>
            <Textarea
              id="comm-notes"
              placeholder="Additional details..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-surface-2 border-border min-h-[60px] resize-none text-sm"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!summary.trim() || createMutation.isPending}
            >
              {createMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Log
            </Button>
          </div>

          {createMutation.error && (
            <p className="text-xs text-destructive">{createMutation.error.message}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
